import React, { useRef, useEffect } from 'react';
import { Upload, Download, Clock, Trash2 } from 'lucide-react';
import { parseSRT, jsonToSRT } from '../utils/srtHelper';

export default function SubtitleEditor({
    subtitles,
    setSubtitles,
    currentTime,
    onSeek
}) {
    const fileInputRef = useRef(null);
    const activeSubtitleRef = useRef(null);
    const containerRef = useRef(null);

    // Auto-scroll to active subtitle
    useEffect(() => {
        if (activeSubtitleRef.current && containerRef.current) {
            activeSubtitleRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [currentTime]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target.result;
            try {
                const parsed = parseSRT(content);
                setSubtitles(parsed);
                alert(`Imported ${parsed.length} subtitles!`);
            } catch (error) {
                console.error("Failed to parse SRT", error);
                alert("Invalid SRT file");
            }
        };
        reader.readAsText(file);
    };

    const handleExport = () => {
        const content = jsonToSRT(subtitles);
        const blob = new Blob([content], { type: 'text/srt' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'subtitles.srt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleTextChange = (index, newText) => {
        const updated = [...subtitles];
        updated[index].text = newText;
        setSubtitles(updated);
    };

    const handleDeleteAll = () => {
        if (window.confirm("Are you sure you want to clear ALL subtitles? This cannot be undone.")) {
            setSubtitles([]);
        }
    };

    return (
        <div className="flex flex-col h-full bg-[#1a1a1a]/50 rounded-xl border border-white/5 overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-white/5 bg-[#1a1a1a]">
                <h3 className="font-bold text-gray-300 flex items-center gap-2">
                    <Clock size={16} className="text-cyan-400" />
                    Subtitles ({subtitles.length})
                </h3>
                <div className="flex gap-2">
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                        title="Import SRT"
                    >
                        <Upload size={18} />
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".srt"
                        className="hidden"
                    />
                    <button
                        onClick={handleExport}
                        disabled={subtitles.length === 0}
                        className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                        title="Export SRT"
                    >
                        <Download size={18} />
                    </button>

                    <button
                        onClick={handleDeleteAll}
                        disabled={subtitles.length === 0}
                        className="p-2 hover:bg-red-500/10 rounded-lg text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                        title="Clear All Subtitles"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>

            {/* List */}
            <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                {subtitles.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">
                        <Upload size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No subtitles yet.</p>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-cyan-400 hover:text-cyan-300 text-sm mt-2"
                        >
                            Import .SRT file
                        </button>
                    </div>
                ) : (
                    subtitles.map((sub, index) => {
                        const isActive = currentTime >= sub.startTime && currentTime <= sub.endTime;
                        return (
                            <div
                                key={sub.id}
                                ref={isActive ? activeSubtitleRef : null}
                                className={`group relative p-3 rounded-lg border transition-all ${isActive
                                    ? 'bg-cyan-500/10 border-cyan-500/50 shadow-[0_0_15px_rgba(0,100,255,0.1)]'
                                    : 'bg-black/20 border-white/5 hover:border-white/10'
                                    }`}
                            >
                                <div
                                    className="flex justify-between items-center mb-1 cursor-pointer"
                                    onClick={() => onSeek(sub.startTime)}
                                >
                                    <span className={`text-xs font-mono ${isActive ? 'text-cyan-400' : 'text-gray-500'}`}>
                                        {sub.start}
                                    </span>
                                    <span className="text-xs text-gray-600 group-hover:text-gray-500">
                                        #{index + 1}
                                    </span>
                                </div>
                                <textarea
                                    value={sub.text}
                                    onChange={(e) => handleTextChange(index, e.target.value)}
                                    className={`w-full bg-transparent resize-none focus:outline-none text-sm leading-relaxed ${isActive ? 'text-white font-medium' : 'text-gray-400'
                                        }`}
                                    rows={Math.max(2, sub.text.split('\n').length)}
                                />
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
